import { TooltipButton } from './TooltipButton';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  children?: React.ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  children
}) => {
  const { t } = useTranslation();
  const finalConfirmLabel = confirmLabel || t('common.save'); // Fallback to Save if not provided
  const finalCancelLabel = cancelLabel || t('common.cancel');

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed top-0 left-0 right-0 bottom-0 z-[500] flex flex-col items-center justify-center p-4 sm:p-6 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 flex flex-col items-center text-center space-y-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                variant === 'danger' ? 'bg-rose-50 text-rose-500' : 
                variant === 'warning' ? 'bg-amber-50 text-amber-500' : 
                'bg-indigo-50 text-indigo-500'
              }`}>
                <AlertTriangle size={40} strokeWidth={2.5} />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-2xl font-black text-[#1a2b3c] tracking-tight">{title}</h3>
                <p className="text-sm font-medium text-gray-500 leading-relaxed">
                  {message}
                </p>
              </div>

              {children}

              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className={`w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg transition-all active:scale-95 ${
                    variant === 'danger' ? 'bg-rose-500 text-white shadow-rose-200 hover:bg-rose-600' :
                    variant === 'warning' ? 'bg-amber-500 text-white shadow-amber-200 hover:bg-amber-600' :
                    'bg-indigo-500 text-white shadow-indigo-200 hover:bg-indigo-600'
                  }`}
                >
                  {finalConfirmLabel}
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-all"
                >
                  {finalCancelLabel}
                </button>
              </div>
            </div>
            
            <TooltipButton label={t('common.close', { defaultValue: 'Close' })}
              onClick={onClose}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-gray-300 hover:text-gray-500 transition-all z-50 rounded-full hover:bg-gray-50"
              aria-label={t('auth.close')}
            >
              <X size={24} />
            </TooltipButton>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
